import { useState, useEffect, useRef } from "react";
import { ClerkProvider, useClerk, useUser, useSignIn, useSignUp } from "@clerk/clerk-react";

const CLERK_KEY = "pk_live_Y2xlcmsuZmx5dmFudGFnZS5pbyQ";
const API_BASE = "https://api.flyvantage.io/api";

const FONT = `@import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,600;1,300;1,400&family=DM+Mono:wght@300;400;500&display=swap');`;

const CSS = `
* { box-sizing: border-box; margin: 0; padding: 0; }
body { background: #0c0c0e; font-family: 'DM Mono', monospace; }
@keyframes fadeUp { from { opacity:0; transform:translateY(16px); } to { opacity:1; transform:translateY(0); } }
@keyframes shimmer { 0%,100%{opacity:.4} 50%{opacity:1} }
@keyframes spin { to { transform: rotate(360deg); } }
@keyframes glow-pulse { 0%,100%{box-shadow:0 0 20px #c9a96e20} 50%{box-shadow:0 0 40px #c9a96e40} }
textarea:focus, input:focus { outline: none; }
::-webkit-scrollbar { width: 4px; }
::-webkit-scrollbar-track { background: #0c0c0e; }
::-webkit-scrollbar-thumb { background: #2a2720; border-radius: 2px; }
`;

const WATCHES = [
  { id:"w1", label:"JFK → NCE", detail:"Aug 4 · Business · All programs", type:"airline", active:true, lastCheck:"2m ago", status:"watching", programs:["United","Alaska","American"] },
  { id:"w2", label:"JFK → CDG", detail:"Aug 8 · Business · Flying Blue", type:"airline", active:true, lastCheck:"2m ago", status:"found", programs:["Air France"], aiInsight:"Sweet spot alert: AF/KLM business on own metal at 55k Flying Blue points is exceptional value. Fees are low at \$89. Book directly on airfrance.com — availability shows now but typically lasts under 2 hours." },
  { id:"w3", label:"7Pines Sardinia", detail:"Aug 9–10 · Award · Hyatt", type:"hotel", active:true, lastCheck:"8m ago", status:"watching", programs:["Hyatt"] },
];

const ALERTS = [
  { id:"a1", watch:"JFK → CDG Aug 8", program:"Air France / Flying Blue", detail:"Business · 55,000 pts + \$89 fees · 2 seats", time:"Today 9:14 AM", urgent:true, aiInsight:"This is one of the best redemptions in miles. Flying Blue at 55k on Air France own metal to Paris is a sweet spot." },
  { id:"a2", watch:"JFK → NCE Aug 4", program:"Alaska / British Airways", detail:"Business · 45,000 pts + \$1,018 fees · 9 seats", time:"Yesterday 4:32 PM", urgent:false, aiInsight:"Mixed verdict: 45k Alaska miles is a great rate but BA fuel surcharges are steep at \$1,018." },
];

const FLEXIBLE_DATE_EXAMPLES = [
  "Any weekend in August, business to Paris under 60k miles",
  "Best dates in July to fly business to Tokyo",
  "Cheapest week in September for NYC to London",
];

const EXAMPLE_PROMPTS = [
  "Alert me when business class to Paris opens under 60k miles in August",
  "Watch for award nights at any Hyatt in Sardinia this summer",
  "Notify me when JFK to Tokyo business drops below 80k United miles",
];

function Dot({ color="#c9a96e", pulse=false, size=7 }) {
  return <span style={{ display:"inline-block", width:size, height:size, borderRadius:"50%", background:color, boxShadow:pulse?`0 0 8px \${color}`:"none", animation:pulse?"shimmer 2s ease infinite":"none", flexShrink:0 }}/>;
}
function Tag({ children, color="#c9a96e" }) {
  return <span style={{ fontSize:14, fontWeight:500, letterSpacing:"0.1em", color, background:color+"18", border:`1px solid \${color}30`, borderRadius:3, padding:"2px 6px", textTransform:"uppercase" }}>{children}</span>;
}

async function parseWatchIntent(text) {
  const response = await fetch("/api/ai", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      model: "claude-sonnet-4-20250514",
      max_tokens: 1000,
      system: `Parse travel watch requests into JSON only. No markdown.
For airline: {"type":"airline","label":"short label","origin":"IATA","destination":"IATA","date":"YYYY-MM-DD","cabin":"economy|business|first","confidence":0-100}
For hotel: {"type":"hotel","label":"short label","property":"name","program":"hyatt|marriott|hilton","check_in":"YYYY-MM-DD","check_out":"YYYY-MM-DD","confidence":0-100}
Use 2026 for relative dates.`,
      messages: [{ role:"user", content: text }]
    })
  });
  const data = await response.json();
  const raw = data.content?.[0]?.text || "{}";
  try { return JSON.parse(raw.replace(/```json|```/g,"").trim()); }
  catch { return { error: true }; }
}

async function interpretAlert(alert) {
  if (alert.aiInsight) return alert.aiInsight;
  const response = await fetch("/api/ai", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      model: "claude-sonnet-4-20250514",
      max_tokens: 500,
      system: "You are an expert in airline miles and points. Give actionable advice in 2-3 sentences. Be direct and specific.",
      messages: [{ role:"user", content: `Award space found: ${alert.watch} on ${alert.program}. ${alert.detail}. What should the user know?` }]
    })
  });
  const data = await response.json();
  return data.content?.[0]?.text || "";
}

async function optimizePoints(alert, userPrograms) {
  const response = await fetch("/api/ai", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({
      model: "claude-sonnet-4-20250514",
      max_tokens: 1000,
      system: `You are an expert in airline miles, hotel points, and credit card transfer partners. 
When given a flight or hotel alert, tell the user EXACTLY how to book it with points.
Be specific: name the exact program, exact points needed, exact transfer partners that work.
Format as 3-4 bullet points. Be direct and actionable. No fluff.`,
      messages: [{ 
        role: "user", 
        content: `Alert: ${alert.watch} on ${alert.program}. ${alert.detail}.
User has these programs: ${userPrograms || "Chase Ultimate Rewards, Amex Membership Rewards, United MileagePlus, Delta SkyMiles"}.
How should they book this? Give specific point amounts and transfer options.`
      }]
    })
  });
  const data = await response.json();
  return data.content?.[0]?.text || "";
}

function AICommentary({ text, loading }) {
  if (loading) return (
    <div style={{ display:"flex", gap:8, alignItems:"center", padding:"10px 12px", background:"#0f0e0b", borderRadius:6, marginTop:10, border:"1px solid #2a2720" }}>
      <div style={{ width:14, height:14, border:"1.5px solid #c9a96e40", borderTopColor:"#c9a96e", borderRadius:"50%", animation:"spin 1s linear infinite", flexShrink:0 }}/>
      <span style={{ color:"#b8b0a8", fontSize:11 }}>Analyzing this opportunity...</span>
    </div>
  );
  if (!text) return null;
  return (
    <div style={{ padding:"10px 12px", background:"#0f0e0b", borderRadius:6, marginTop:10, border:"1px solid #c9a96e20" }}>
      <div style={{ display:"flex", gap:6, alignItems:"flex-start" }}>
        <span style={{ color:"#c9a96e", fontSize:14, flexShrink:0, marginTop:1 }}>◈</span>
        <p style={{ color:"#b8b0a8", fontSize:14, lineHeight:1.7 }}>{text}</p>
      </div>
    </div>
  );
}

function WatchCard({ watch, onDelete, onPause }) {
  const [hov, setHov] = useState(false);
  const [showAI, setShowAI] = useState(false);
  const [aiText, setAiText] = useState(watch.aiInsight || "");
  const [aiLoading, setAiLoading] = useState(false);
  const statusColor = watch.status==="found"?"#3d8c5a":watch.status==="paused"?"#9a9080":"#c9a96e";

  const handleAIClick = async () => {
    setShowAI(v => !v);
    if (!aiText && watch.status === "found") {
      setAiLoading(true);
      const text = await interpretAlert({ watch: watch.label, program: watch.programs[0], detail: watch.detail });
      setAiText(text);
      setAiLoading(false);
    }
  };

  return (
    <div onMouseEnter={()=>setHov(true)} onMouseLeave={()=>setHov(false)} style={{
      background: hov?"#1e1c18":"#111008",
      border: watch.status==="found"?"1px solid #3d8c5a40":"1px solid #1e1c18",
      borderRadius:10, padding:"16px 18px", transition:"all .2s",
      boxShadow: watch.status==="found"?"0 0 30px #3d8c5a10":"none"
    }}>
      <div style={{ display:"flex", gap:14, alignItems:"flex-start" }}>
        <div style={{
          width:34, height:34, borderRadius:7, flexShrink:0,
          background: watch.type==="airline"?"#c9a96e12":"#6e9ec912",
          border:`1px solid ${watch.type==="airline"?"#c9a96e25":"#6e9ec925"}`,
          display:"flex", alignItems:"center", justifyContent:"center", fontSize:15
        }}>{watch.type==="airline"?"✈":"🏨"}</div>
        <div style={{ flex:1, minWidth:0 }}>
          <div style={{ display:"flex", alignItems:"center", gap:8, flexWrap:"wrap", marginBottom:3 }}>
            <Dot color={statusColor} pulse={watch.active && watch.status!=="paused"} />
            <span style={{ color:"#f0ede6", fontSize:14, fontFamily:"'Cormorant Garamond', serif", fontWeight:600, letterSpacing:"0.02em" }}>{watch.label}</span>
            <Tag color={statusColor}>{watch.status==="found"?"Space Found":watch.status==="paused"?"Paused":"Watching"}</Tag>
          </div>
          <div style={{ color:"#d8d0c0", fontSize:14, marginBottom:6 }}>{watch.detail}</div>
          <div style={{ display:"flex", gap:4, flexWrap:"wrap" }}>
            {watch.programs.map(p => (
              <span key={p} style={{ fontSize:14, color:"#d0c8b8", background:"#1e1c18", border:"1px solid #1e1c18", borderRadius:3, padding:"1px 5px" }}>{p}</span>
            ))}
          </div>
        </div>
        <div style={{ display:"flex", flexDirection:"column", alignItems:"flex-end", gap:6, flexShrink:0 }}>
          <span style={{ fontSize:14, color:"#d0c8b0" }}>{watch.lastCheck}</span>
          <div style={{ display:"flex", gap:5 }}>
            {watch.status==="found" && (
              <button onClick={handleAIClick} style={{
                background: showAI?"#c9a96e15":"none",
                border:`1px solid ${showAI?"#c9a96e50":"#2a2720"}`,
                color: showAI?"#c9a96e":"#b0a898",
                borderRadius:5, padding:"3px 8px", fontSize:14,
                fontFamily:"'DM Mono', monospace", cursor:"pointer", transition:"all .15s"
              }}>◈ AI INSIGHT</button>
            )}
            <button onClick={()=>onPause(watch.id)} style={{ background:"none", border:"1px solid #2a2720", color:"#d0c8b8", borderRadius:5, padding:"3px 8px", fontSize:14, cursor:"pointer", fontFamily:"'DM Mono', monospace" }}>{watch.active?"Pause":"Resume"}</button>
            <button onClick={()=>onDelete(watch.id)} style={{ background:"none", border:"1px solid #2a1818", color:"#7a4040", borderRadius:5, padding:"3px 8px", fontSize:14, cursor:"pointer", fontFamily:"'DM Mono', monospace" }}>✕</button>
          </div>
        </div>
      </div>
      {showAI && <AICommentary text={aiText} loading={aiLoading} />}
    </div>
  );
}

function AlertRow({ alert }) {
  const [showAI, setShowAI] = useState(false);
  const [aiText, setAiText] = useState(alert.aiInsight || "");
  const [aiLoading, setAiLoading] = useState(false);
  const [showOptimizer, setShowOptimizer] = useState(false);
  const [optimizerText, setOptimizerText] = useState("");
  const [optimizerLoading, setOptimizerLoading] = useState(false);

  const handleAI = async () => {
    setShowAI(v => !v);
    if (!aiText) {
      setAiLoading(true);
      const text = await interpretAlert(alert);
      setAiText(text);
      setAiLoading(false);
    }
  };

  const handleOptimize = async () => {
    setShowOptimizer(v => !v);
    if (!optimizerText) {
      setOptimizerLoading(true);
      const text = await optimizePoints(alert);
      setOptimizerText(text);
      setOptimizerLoading(false);
    }
  };

  return (
    <div style={{ padding:"14px 0", borderBottom:"1px solid #161410" }}>
      <div style={{ display:"flex", gap:12, alignItems:"flex-start" }}>
        <div style={{
          width:30, height:30, borderRadius:6, flexShrink:0,
          background: alert.urgent?"#3d8c5a18":"#c9a96e12",
          border:`1px solid ${alert.urgent?"#3d8c5a35":"#c9a96e25"}`,
          display:"flex", alignItems:"center", justifyContent:"center", fontSize:13
        }}>{alert.urgent?"🎯":"✈"}</div>
        <div style={{ flex:1 }}>
          <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:2 }}>
            <span style={{ color:"#f0ede6", fontSize:15, fontFamily:"'Cormorant Garamond', serif", fontWeight:600 }}>{alert.watch}</span>
            {alert.urgent && <Tag color="#3d8c5a">New</Tag>}
          </div>
          <div style={{ color:"#c9a96e", fontSize:14, marginBottom:2 }}>{alert.program}</div>
          <div style={{ color:"#d8d0c0", fontSize:10 }}>{alert.detail}</div>
          {showAI && <AICommentary text={aiText} loading={aiLoading} />}
          {showOptimizer && <AICommentary text={optimizerText} loading={optimizerLoading} />}
        </div>
        <div style={{ display:"flex", flexDirection:"column", alignItems:"flex-end", gap:6, flexShrink:0 }}>
          <span style={{ color:"#d0c8b0", fontSize:10 }}>{alert.time}</span>
          <button onClick={handleAI} style={{
            background: showAI?"#c9a96e15":"none",
            border:`1px solid ${showAI?"#c9a96e40":"#2a2720"}`,
            color: showAI?"#c9a96e":"#a09888",
            borderRadius:5, padding:"3px 8px", fontSize:14,
            fontFamily:"'DM Mono', monospace", cursor:"pointer", transition:"all .15s"
          }}>◈ EXPLAIN</button>
        </div>
      </div>
    </div>
  );
}

function AIWatchCreator({ onAdd, onClose }) {
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [parsed, setParsed] = useState(null);
  const [error, setError] = useState("");
  const [confirmed, setConfirmed] = useState(false);

  const handleParse = async () => {
    if (!input.trim()) return;
    setLoading(true);
    setError("");
    setParsed(null);
    const result = await parseWatchIntent(input);
    setLoading(false);
    if (result.error) { setError("Couldn't parse that. Try being more specific."); return; }
    if (result.confidence < 60) { setError("Need more details. Try adding a date or destination."); return; }
    setParsed(result);
  };

  const handleConfirm = () => {
    if (!parsed) return;
    onAdd(parsed);
    setConfirmed(true);
    setTimeout(onClose, 1200);
  };

  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,.85)", display:"flex", alignItems:"center", justifyContent:"center", zIndex:300, backdropFilter:"blur(10px)" }}
      onClick={e => e.target===e.currentTarget && onClose()}>
      <div style={{ background:"#0f0e0b", border:"1px solid #2a2720", borderRadius:14, padding:28, width:520, maxWidth:"92vw", boxShadow:"0 40px 100px #000c" }}>
        {confirmed ? (
          <div style={{ textAlign:"center", padding:"20px 0" }}>
            <div style={{ fontSize:32, marginBottom:12 }}>✓</div>
            <div style={{ color:"#3d8c5a", fontSize:13 }}>Watch created</div>
          </div>
        ) : (
          <>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:20 }}>
              <div>
                <div style={{ color:"#f0ede6", fontSize:15, fontFamily:"'Cormorant Garamond', serif", fontWeight:600, marginBottom:3 }}>◈ AI Watch Creator</div>
                <div style={{ color:"#d0c8b8", fontSize:10 }}>Describe what you want to watch in plain English</div>
              </div>
              <button onClick={onClose} style={{ background:"none", border:"none", color:"#d0c8b8", fontSize:18, cursor:"pointer" }}>×</button>
            </div>
            <textarea value={input} onChange={e => setInput(e.target.value)}
              onKeyDown={e => { if (e.key==="Enter" && !e.shiftKey) { e.preventDefault(); handleParse(); } }}
              placeholder="e.g. Alert me when business class opens to Paris in August under 60k miles..."
              style={{ width:"100%", background:"#141310", border:"1px solid #2a2720", borderRadius:8, padding:"12px", color:"#f0ede6", fontSize:14, fontFamily:"'DM Mono', monospace", resize:"none", height:80, lineHeight:1.6 }}
            />
            <div style={{ marginTop:10, marginBottom:16 }}>
              <div style={{ color:"#d0c8b0", fontSize:14, letterSpacing:"0.08em", marginBottom:6 }}>EXAMPLES</div>
              <div style={{ display:"flex", flexWrap:"wrap", gap:5 }}>
                {EXAMPLE_PROMPTS.map(p => (
                  <button key={p} onClick={() => setInput(p)} style={{ background:"#141310", border:"1px solid #2a2720", color:"#d0c8b8", borderRadius:5, padding:"4px 8px", fontSize:14, fontFamily:"'DM Mono', monospace", cursor:"pointer", textAlign:"left" }}>{p}</button>
                ))}
              </div>
            </div>
            {error && <div style={{ background:"#2a1818", border:"1px solid #4a2020", borderRadius:6, padding:"8px 12px", marginBottom:12 }}><span style={{ color:"#c87070", fontSize:11 }}>{error}</span></div>}
            {parsed && !error && (
              <div style={{ background:"#0c1a12", border:"1px solid #3d8c5a40", borderRadius:8, padding:14, marginBottom:16 }}>
                <div style={{ color:"#3d8c5a", fontSize:14, letterSpacing:"0.1em", marginBottom:10 }}>✓ PARSED</div>
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8 }}>
                  {Object.entries(parsed).filter(([k]) => !["confidence","type"].includes(k)).map(([k,v]) => (
                    <div key={k}>
                      <div style={{ color:"#6a9070", fontSize:14, letterSpacing:"0.08em", marginBottom:2 }}>{k.toUpperCase()}</div>
                      <div style={{ color:"#a0c8a0", fontSize:11 }}>{Array.isArray(v)?v.join(", "):v}</div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <div style={{ display:"flex", gap:8 }}>
              <button onClick={handleParse} disabled={loading || !input.trim()} style={{ flex:1, background:"#c9a96e15", border:"1px solid #c9a96e40", color:"#c9a96e", borderRadius:8, padding:"10px", fontSize:14, fontFamily:"'DM Mono', monospace", cursor:"pointer", letterSpacing:"0.06em", display:"flex", alignItems:"center", justifyContent:"center", gap:8 }}>
                {loading && <div style={{ width:12, height:12, border:"1.5px solid #c9a96e40", borderTopColor:"#c9a96e", borderRadius:"50%", animation:"spin 1s linear infinite" }}/>}
                {loading ? "SETTING..." : "SET ALERT"}
              </button>
              {parsed && <button onClick={handleConfirm} style={{ flex:1, background:"linear-gradient(135deg,#3d8c5a,#2a6a40)", border:"none", color:"#f0ede6", borderRadius:8, padding:"10px", fontSize:14, fontFamily:"'DM Mono', monospace", cursor:"pointer", letterSpacing:"0.06em", fontWeight:500 }}>START WATCHING ✓</button>}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

function Landing({ onEnter }) {
  const [loaded, setLoaded] = useState(false);
  const [demoInput, setDemoInput] = useState("");
  const [demoLoading, setDemoLoading] = useState(false);
  const [demoResult, setDemoResult] = useState(null);
  useEffect(() => { setTimeout(() => setLoaded(true), 80); }, []);

  const handleDemo = async () => {
    if (!demoInput.trim()) return;
    setDemoLoading(true);
    setDemoResult(null);
    const result = await parseWatchIntent(demoInput);
    setDemoLoading(false);
    setDemoResult(result);
  };

  return (
    <div style={{ minHeight:"100vh", background:"#0c0c0e", display:"flex", flexDirection:"column", position:"relative", overflow:"hidden" }}>
      <div style={{ position:"absolute", inset:0, opacity:.03, backgroundImage:"linear-gradient(#c9a96e 1px,transparent 1px),linear-gradient(90deg,#c9a96e 1px,transparent 1px)", backgroundSize:"80px 80px", pointerEvents:"none" }}/>
      <div style={{ position:"absolute", top:"15%", left:"50%", transform:"translateX(-50%)", width:800, height:500, borderRadius:"50%", background:"radial-gradient(ellipse,#c9a96e06 0%,transparent 70%)", pointerEvents:"none" }}/>
      <nav style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"22px 48px", position:"relative", zIndex:10 }}>
        <div style={{ display:"flex", alignItems:"center", gap:10 }}>
          <div style={{ width:26, height:26, borderRadius:6, background:"linear-gradient(135deg,#c9a96e,#a07840)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:14, color:"#0c0c0e" }}>◈</div>
          <span style={{ color:"#f0ede6", fontSize:14, letterSpacing:"0.25em", fontWeight:500 }}>VANTAGE</span>
        </div>
        <button onClick={onEnter} style={{ background:"none", border:"1px solid #2a2720", color:"#d8d0c0", borderRadius:8, padding:"7px 16px", fontSize:14, fontFamily:"'DM Mono', monospace", cursor:"pointer", letterSpacing:"0.08em" }}>Open app →</button>
      </nav>
      <div style={{ flex:1, display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center", textAlign:"center", padding:"60px 24px 40px", position:"relative", zIndex:10 }}>
        <div style={{ display:"inline-flex", alignItems:"center", gap:8, background:"#c9a96e0c", border:"1px solid #c9a96e25", borderRadius:20, padding:"5px 14px", marginBottom:28, opacity:loaded?1:0, animation:loaded?"fadeUp .5s ease forwards":"none" }}>
          <Dot color="#c9a96e" pulse size={6} />
          <span style={{ color:"#c9a96e", fontSize:14, letterSpacing:"0.12em" }}>AI-POWERED · CHECKS EVERY 10 MINUTES</span>
        </div>
        <h1 style={{ fontFamily:"'Cormorant Garamond', serif", fontSize:"clamp(48px,7vw,88px)", fontWeight:300, color:"#f0ede6", lineHeight:1.05, marginBottom:20, letterSpacing:"-0.02em", opacity:loaded?1:0, animation:loaded?"fadeUp .6s .08s ease both":"none" }}>
          Award space opens.<br/>Most people miss it.<br/><em style={{ color:"#c9a96e", fontStyle:"italic" }}>You won't.</em>
        </h1>
        <p style={{ color:"#d0c8b8", fontSize:15, maxWidth:440, lineHeight:1.8, marginBottom:40, fontWeight:300, opacity:loaded?1:0, animation:loaded?"fadeUp .6s .15s ease both":"none" }}>
          Flights. Hotels. Cash prices. Points. Vantage watches everything 24/7 — and alerts you the second something opens up.
        </p>
        <div style={{ width:"100%", maxWidth:560, opacity:loaded?1:0, animation:loaded?"fadeUp .6s .22s ease both":"none", marginBottom:40 }}>
          <div style={{ background:"#111008", border:"1px solid #2a2720", borderRadius:12, padding:20, boxShadow:"0 20px 60px #0008" }}>
            <div style={{ color:"#c8b898", fontSize:14, letterSpacing:"0.1em", marginBottom:10 }}>WHERE DO YOU WANT TO GO?</div>
            <div style={{ display:"flex", gap:8 }}>
              <input value={demoInput} onChange={e => setDemoInput(e.target.value)} onKeyDown={e => e.key==="Enter" && handleDemo()}
                placeholder="e.g. Business class to Rome in August under 70k miles..."
                style={{ flex:1, background:"#0c0c0e", border:"1px solid #2a2720", borderRadius:7, padding:"10px 12px", color:"#f0ede6", fontSize:14, fontFamily:"'DM Mono', monospace" }}
              />
              <button onClick={handleDemo} disabled={demoLoading} style={{ background:"linear-gradient(135deg,#c9a96e,#a07840)", border:"none", color:"#0c0c0e", borderRadius:7, padding:"10px 16px", fontSize:14, fontFamily:"'DM Mono', monospace", cursor:"pointer", fontWeight:500, whiteSpace:"nowrap" }}>
                {demoLoading ? "..." : "SET ALERT →"}
              </button>
            </div>
            {demoLoading && <div style={{ display:"flex", gap:8, alignItems:"center", marginTop:12 }}><div style={{ width:12, height:12, border:"1.5px solid #c9a96e30", borderTopColor:"#c9a96e", borderRadius:"50%", animation:"spin 1s linear infinite" }}/><span style={{ color:"#d8d0c0", fontSize:10 }}>Setting up your alert...</span></div>}
            {demoResult && !demoLoading && (
              <div style={{ marginTop:14, background:"#0c1a12", border:"1px solid #3d8c5a30", borderRadius:8, padding:12 }}>
                <div style={{ color:"#3d8c5a", fontSize:14, letterSpacing:"0.1em", marginBottom:8 }}>✓ WATCH CONFIGURED</div>
                <div style={{ display:"flex", flexWrap:"wrap", gap:12 }}>
                  {Object.entries(demoResult).filter(([k])=>!["confidence","error","raw"].includes(k)).map(([k,v])=>(
                    <div key={k}><div style={{ color:"#5a8060", fontSize:14, letterSpacing:"0.1em" }}>{k.toUpperCase()}</div><div style={{ color:"#80b090", fontSize:14, marginTop:1 }}>{Array.isArray(v)?v.join(", "):String(v)}</div></div>
                  ))}
                </div>
                <button onClick={onEnter} style={{ marginTop:12, width:"100%", background:"linear-gradient(135deg,#c9a96e,#a07840)", border:"none", color:"#0c0c0e", borderRadius:6, padding:"10px", fontSize:14, fontFamily:"'DM Mono', monospace", cursor:"pointer", letterSpacing:"0.06em", fontWeight:500 }}>
                  CREATE FREE ACCOUNT →
                </button>
              </div>
            )}
          </div>
        </div>
        <div style={{ display:"flex", gap:48, opacity:loaded?1:0, animation:loaded?"fadeUp .6s .3s ease both":"none" }}>
          {[["15+","Airline programs"],["10min","Check interval"],["$0","To get started"]].map(([n,l]) => (
            <div key={n} style={{ textAlign:"center" }}>
              <div style={{ color:"#c9a96e", fontSize:24, fontFamily:"'Cormorant Garamond', serif", fontWeight:600 }}>{n}</div>
              <div style={{ color:"#a09880", fontSize:14, letterSpacing:"0.08em", marginTop:4 }}>{l}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ── Airport lookup ────────────────────────────────────────────────────────
const AIRPORTS = {
  "new york":"JFK","nyc":"JFK","jfk":"JFK","lga":"LGA","ewr":"EWR",
  "los angeles":"LAX","la":"LAX","lax":"LAX",
  "chicago":"ORD","ord":"ORD","mdw":"MDW",
  "miami":"MIA","mia":"MIA","fll":"FLL","fort lauderdale":"FLL",
  "san francisco":"SFO","sfo":"SFO","oakland":"OAK",
  "london":"LHR","lhr":"LHR","lgw":"LGW","gatwick":"LGW","heathrow":"LHR",
  "paris":"CDG","cdg":"CDG","ory":"ORY",
  "rome":"FCO","fco":"FCO","rome fiumicino":"FCO",
  "milan":"MXP","mxp":"MXP","linate":"LIN",
  "tokyo":"NRT","nrt":"NRT","hnd":"HND","narita":"NRT",
  "dubai":"DXB","dxb":"DXB",
  "singapore":"SIN","sin":"SIN",
  "sydney":"SYD","syd":"SYD",
  "toronto":"YYZ","yyz":"YYZ",
  "amsterdam":"AMS","ams":"AMS",
  "frankfurt":"FRA","fra":"FRA",
  "madrid":"MAD","mad":"MAD",
  "barcelona":"BCN","bcn":"BCN",
  "nice":"NCE","nce":"NCE",
  "sardinia":"CAG","cagliari":"CAG","cag":"CAG",
  "lisbon":"LIS","lis":"LIS",
  "athens":"ATH","ath":"ATH",
  "tel aviv":"TLV","tlv":"TLV",
  "bangkok":"BKK","bkk":"BKK",
  "hong kong":"HKG","hkg":"HKG",
  "seoul":"ICN","icn":"ICN",
  "mexico city":"MEX","mex":"MEX",
  "cancun":"CUN","cun":"CUN",
  "boston":"BOS","bos":"BOS",
  "washington":"DCA","dca":"DCA","iad":"IAD","dulles":"IAD",
  "seattle":"SEA","sea":"SEA",
  "denver":"DEN","den":"DEN",
  "atlanta":"ATL","atl":"ATL",
  "dallas":"DFW","dfw":"DFW",
  "houston":"IAH","iah":"IAH",
  "phoenix":"PHX","phx":"PHX",
  "las vegas":"LAS","las":"LAS",
  "orlando":"MCO","mco":"MCO",
};

function lookupAirport(input) {
  const key = input.toLowerCase().trim();
  return AIRPORTS[key] || input.toUpperCase().slice(0,3);
}

function AirportInput({ value, onChange, placeholder }) {
  const [raw, setRaw] = useState(value);
  const [suggestion, setSuggestion] = useState("");

  const handle = (v) => {
    setRaw(v);
    const code = lookupAirport(v);
    setSuggestion(code !== v.toUpperCase().slice(0,3) ? code : "");
    onChange(code);
  };

  return (
    <div style={{ position:"relative" }}>
      <input
        value={raw}
        onChange={e => handle(e.target.value)}
        placeholder={placeholder}
        style={{
          background:"#111008", border:"1px solid #2a2720", borderRadius:7,
          padding:"10px 13px", color:"#f0ede6", fontSize:14,
          fontFamily:"'DM Mono', monospace", width:120
        }}
      />
      {suggestion && (
        <div style={{
          position:"absolute", top:"100%", left:0, marginTop:4,
          background:"#1a1916", border:"1px solid #c9a96e40",
          borderRadius:6, padding:"5px 10px", zIndex:50,
          color:"#c9a96e", fontSize:12, cursor:"pointer", whiteSpace:"nowrap"
        }} onClick={() => { setRaw(suggestion); onChange(suggestion); setSuggestion(""); }}>
          → {suggestion}
        </div>
      )}
    </div>
  );
}

// ── Calendar View ────────────────────────────────────────────────────────────
function CalendarView() {
  const [origin, setOrigin] = useState("JFK");
  const [destination, setDestination] = useState("");
  const [month, setMonth] = useState(() => {
    const d = new Date();
    return `${d.getFullYear()}-${String(d.getMonth()+2).padStart(2,"0")}`;
  });
  const [cabin, setCabin] = useState("business");
  const [loading, setLoading] = useState(false);
  const [calData, setCalData] = useState({});
  const [searched, setSearched] = useState(false);
  const [calMode, setCalMode] = useState("points"); // "points" or "cash"
  const [selectedDate, setSelectedDate] = useState(null);
  const [dateAI, setDateAI] = useState("");
  const [dateAILoading, setDateAILoading] = useState(false);

  useEffect(() => {
    if (!selectedDate) return;
    setDateAI("");
    setDateAILoading(true);
    const { dateStr, d, origin, destination, cabin } = selectedDate;
    const prompt = `Flight: ${origin} to ${destination} on ${dateStr}, ${cabin} class.
Available via ${d?.program || "multiple programs"} for ${d?.points ? d.points.toLocaleString() + " points" : "cash"}.
Tell me: 1) Which airlines operate this route 2) Exactly how to book with points (which program, transfer partners) 3) Is this good value? 4) Any tips. Be specific and direct.`;
    
    fetch("/api/ai", {
      method:"POST",
      headers:{"Content-Type":"application/json"},
      body: JSON.stringify({
        model:"claude-sonnet-4-20250514",
        max_tokens:600,
        system:"You are an expert in airline miles and award booking. Give specific, actionable advice. Name exact airlines, programs, and point amounts.",
        messages:[{role:"user",content:prompt}]
      })
    }).then(r=>r.json()).then(data=>{
      setDateAI(data.content?.[0]?.text||"");
      setDateAILoading(false);
    }).catch(()=>setDateAILoading(false));
  }, [selectedDate]);

  const search = async () => {
    if (!destination) return;
    setLoading(true);
    setSearched(true);
    try {
      const [year, mon] = month.split("-").map(Number);
      const daysInMonth = new Date(year, mon, 0).getDate();
      const results = {};
      // Check first 30 days in batches via Seats.aero
      const res = await fetch(`https://api.flyvantage.io/api/calendar?origin=${origin}&destination=${destination}&month=${month}&cabin=${cabin}`);
      console.log("Calendar API response status:", res.status);
      if (res.ok) {
        const data = await res.json();
        data.forEach(d => { results[d.date] = d; });
      }
      setCalData(results);
    } catch(e) {
      console.error(e);
    }
    setLoading(false);
  };

  const [year, mon] = month.split("-").map(Number);
  const daysInMonth = new Date(year, mon, 0).getDate();
  const firstDay = new Date(year, mon-1, 1).getDay();
  const monthName = new Date(year, mon-1, 1).toLocaleString("default", {month:"long", year:"numeric"});

  const inp = {
    background:"#111008", border:"1px solid #2a2720", borderRadius:7,
    padding:"10px 13px", color:"#f0ede6", fontSize:14,
    fontFamily:"'DM Mono', monospace"
  };

  return (
    <div>
      {/* Search bar */}
      <div style={{ background:"#111008", border:"1px solid #1e1c18", borderRadius:12, padding:20, marginBottom:20 }}>
        <div style={{ display:"flex", gap:10, flexWrap:"wrap", alignItems:"flex-end" }}>
          <div>
            <div style={{ color:"#b0a898", fontSize:11, marginBottom:5, letterSpacing:"0.08em" }}>FROM</div>
            <AirportInput value={origin} onChange={setOrigin} placeholder="JFK or New York"/>
          </div>
          <div style={{ color:"#c9a96e", fontSize:20, paddingBottom:10 }}>→</div>
          <div>
            <div style={{ color:"#b0a898", fontSize:11, marginBottom:5, letterSpacing:"0.08em" }}>TO</div>
            <AirportInput value={destination} onChange={setDestination} placeholder="CDG or Paris"/>
          </div>
          <div>
            <div style={{ color:"#b0a898", fontSize:11, marginBottom:5, letterSpacing:"0.08em" }}>MONTH</div>
            <input type="month" value={month} onChange={e=>setMonth(e.target.value)} style={{...inp, width:160}}/>
          </div>
          <div>
            <div style={{ color:"#b0a898", fontSize:11, marginBottom:5, letterSpacing:"0.08em" }}>CABIN</div>
            <select value={cabin} onChange={e=>setCabin(e.target.value)} style={{...inp, width:140, cursor:"pointer"}}>
              <option value="economy">Economy</option>
              <option value="premium_economy">Prem. Eco</option>
              <option value="business">Business</option>
              <option value="first">First</option>
            </select>
          </div>
          <button onClick={search} disabled={loading||!destination} style={{
            background:"linear-gradient(135deg,#c9a96e,#a07840)", border:"none",
            color:"#0c0c0e", borderRadius:8, padding:"10px 20px",
            fontSize:12, fontFamily:"'DM Mono', monospace",
            cursor: loading||!destination?"not-allowed":"pointer",
            fontWeight:500, letterSpacing:"0.06em", height:42
          }}>
            {loading ? "SEARCHING..." : "SEARCH →"}
          </button>
        </div>

        {/* Mode Toggle */}
        <div style={{ display:"flex", gap:0, marginTop:14, background:"#0c0c0e", borderRadius:8, padding:3, width:"fit-content" }}>
          {["points","cash"].map(m => (
            <button key={m} onClick={() => setCalMode(m)} style={{
              background: calMode===m ? "linear-gradient(135deg,#c9a96e,#a07840)" : "none",
              border:"none", color: calMode===m ? "#0c0c0e" : "#6a6458",
              borderRadius:6, padding:"6px 18px", fontSize:11,
              fontFamily:"'DM Mono', monospace", cursor:"pointer",
              fontWeight: calMode===m ? 500 : 400, letterSpacing:"0.06em",
              textTransform:"uppercase"
            }}>{m === "points" ? "⭐ Points" : "💵 Cash"}</button>
          ))}
        </div>

        {/* Legend */}
        <div style={{ display:"flex", gap:20, marginTop:14, flexWrap:"wrap" }}>
          {calMode==="points" ? [["#3d8c5a","Award space available"],["#161410","Not available"]].map(([c,l])=>(
            <div key={l} style={{ display:"flex", alignItems:"center", gap:6 }}>
              <div style={{ width:12, height:12, borderRadius:3, background:c }}/>
              <span style={{ color:"#b0a898", fontSize:11 }}>{l}</span>
            </div>
          )) : [["#6e9ec9","Cash price available"],["#161410","Not available"]].map(([c,l])=>(
            <div key={l} style={{ display:"flex", alignItems:"center", gap:6 }}>
              <div style={{ width:12, height:12, borderRadius:3, background:c }}/>
              <span style={{ color:"#b0a898", fontSize:11 }}>{l}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Calendar grid */}
      {searched && (
        <div style={{ background:"#111008", border:"1px solid #1e1c18", borderRadius:12, padding:20 }}>
          <div style={{ color:"#f0ede6", fontSize:16, fontFamily:"'Cormorant Garamond', serif", fontWeight:600, marginBottom:16 }}>
            {origin} → {destination} · {monthName}
          </div>

          {/* Day headers */}
          <div style={{ display:"grid", gridTemplateColumns:"repeat(7,1fr)", gap:4, marginBottom:4 }}>
            {["Sun","Mon","Tue","Wed","Thu","Fri","Sat"].map(d=>(
              <div key={d} style={{ color:"#6a6458", fontSize:11, textAlign:"center", padding:"4px 0" }}>{d}</div>
            ))}
          </div>

          {/* Days */}
          <div style={{ display:"grid", gridTemplateColumns:"repeat(7,1fr)", gap:4 }}>
            {Array.from({length: firstDay}).map((_,i) => (
              <div key={`empty-${i}`}/>
            ))}
            {Array.from({length: daysInMonth}).map((_,i) => {
              const day = i + 1;
              const dateStr = `${year}-${String(mon).padStart(2,"0")}-${String(day).padStart(2,"0")}`;
              const d = calData[dateStr];
              const hasAward = d?.award;
              const hasCash = d?.cash;
              const showThis = calMode==="points" ? hasAward : hasCash;
              const bg = calMode==="points" ? (hasAward ? "#3d8c5a" : "#161410") : (hasCash ? "#6e9ec9" : "#161410");
              const textColor = showThis ? "#0c0c0e" : "#4a4840";

              return (
                <div key={day} style={{
                  background: loading ? "#161410" : bg,
                  borderRadius:6, padding:"8px 4px",
                  textAlign:"center", cursor: d?"pointer":"default",
                  transition:"all .2s", minHeight:56,
                  display:"flex", flexDirection:"column", alignItems:"center", justifyContent:"center",
                  border:`1px solid ${hasAward||hasCash?"transparent":"#1e1c18"}`,
                  opacity: loading ? 0.5 : 1
                }}>
                  <div style={{ color: textColor, fontSize:13, fontWeight:500 }}>{day}</div>
                  {calMode==="points" && d?.points && <div style={{ color: textColor, fontSize:10, marginTop:2, opacity:.9 }}>{Math.round(d.points/1000)}k pts</div>}
                  {calMode==="points" && d?.program && <div style={{ color: textColor, fontSize:9, marginTop:1, opacity:.7 }}>{d.program}</div>}
                  {calMode==="cash" && d?.price && <div style={{ color: textColor, fontSize:11, marginTop:2, opacity:.9, fontWeight:500 }}>${Math.round(d.price)}</div>}
                </div>
              );
            })}
          </div>

          {loading && (
            <div style={{ textAlign:"center", padding:"20px 0", color:"#6a6458", fontSize:13 }}>
              Checking availability across all programs...
            </div>
          )}

          {selectedDate && (
            <div style={{ marginTop:20, background:"#0c1a12", border:"1px solid #3d8c5a40", borderRadius:10, padding:20, animation:"fadeUp .3s ease forwards" }}>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:14 }}>
                <div>
                  <div style={{ color:"#f0ede6", fontSize:16, fontFamily:"'Cormorant Garamond', serif", fontWeight:600 }}>
                    {selectedDate.origin} → {selectedDate.destination} · {selectedDate.dateStr}
                  </div>
                  <div style={{ color:"#3d8c5a", fontSize:12, marginTop:3 }}>
                    {selectedDate.d?.points ? `${selectedDate.d.points.toLocaleString()} pts via ${selectedDate.d.program}` : "Cash price available"}
                    {selectedDate.d?.points && ` · ${selectedDate.cabin}`}
                  </div>
                </div>
                <button onClick={() => setSelectedDate(null)} style={{ background:"none", border:"none", color:"#6a6458", fontSize:18, cursor:"pointer" }}>×</button>
              </div>
              {dateAILoading ? (
                <div style={{ display:"flex", gap:8, alignItems:"center" }}>
                  <div style={{ width:14, height:14, border:"1.5px solid #3d8c5a40", borderTopColor:"#3d8c5a", borderRadius:"50%", animation:"spin 1s linear infinite" }}/>
                  <span style={{ color:"#6a6458", fontSize:13 }}>Getting booking guidance...</span>
                </div>
              ) : (
                <div style={{ color:"#c8d8c0", fontSize:13, lineHeight:1.8, whiteSpace:"pre-wrap" }}>{dateAI}</div>
              )}
            </div>
          )}
          {!loading && searched && Object.keys(calData).length === 0 && (
            <div style={{ textAlign:"center", padding:"20px 0", color:"#6a6458", fontSize:13 }}>
              No availability found for this route and month. Try different dates or cabin class.
            </div>
          )}
        </div>
      )}

      {!searched && (
        <div style={{ background:"#111008", border:"1px solid #1e1c18", borderRadius:12, padding:28 }}>
          <div style={{ color:"#f0ede6", fontSize:18, fontFamily:"'Cormorant Garamond', serif", fontWeight:600, marginBottom:16 }}>How the Calendar works</div>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:16 }}>
            {[
              ["📅","Pick a route and month","Enter your origin, destination, and the month you want to travel. Select your cabin class."],
              ["🟢","See availability at a glance","Green days have award space open. Blue days have cash price deals. Gold days have both."],
              ["⚡","Set a watch instantly","See a good date? Click it to create a watch — we'll alert you if availability changes."]
            ].map(([icon,title,desc])=>(
              <div key={title} style={{ background:"#0c0c0e", borderRadius:10, padding:16 }}>
                <div style={{ fontSize:24, marginBottom:10 }}>{icon}</div>
                <div style={{ color:"#f0ede6", fontSize:14, fontWeight:500, marginBottom:8 }}>{title}</div>
                <div style={{ color:"#b0a898", fontSize:13, lineHeight:1.7 }}>{desc}</div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function Tooltip({ text, children }) {
  const [show, setShow] = useState(false);
  return (
    <div style={{ position:"relative", display:"inline-flex" }}
      onMouseEnter={() => setShow(true)}
      onMouseLeave={() => setShow(false)}>
      {children}
      {show && (
        <div style={{
          position:"absolute", bottom:"calc(100% + 8px)", left:"50%",
          transform:"translateX(-50%)", background:"#1a1916",
          border:"1px solid #2a2720", borderRadius:6, padding:"6px 10px",
          color:"#f0ede6", fontSize:11, whiteSpace:"nowrap", zIndex:999,
          boxShadow:"0 4px 16px #000a", pointerEvents:"none"
        }}>
          {text}
          <div style={{ position:"absolute", top:"100%", left:"50%", transform:"translateX(-50%)",
            borderLeft:"5px solid transparent", borderRight:"5px solid transparent",
            borderTop:"5px solid #2a2720" }}/>
        </div>
      )}
    </div>
  );
}

function WelcomeModal({ onClose }) {
  const [step, setStep] = useState(0);
  const steps = [
    {
      icon: "◈",
      title: "Tell us where you want to go",
      desc: "Type any trip in plain English — 'Business class to Paris in August under 60k miles' — and our AI sets up the watch automatically. No forms, no dropdowns."
    },
    {
      icon: "⚡",
      title: "We watch 24/7 so you don't have to",
      desc: "Vantage checks 15+ airline programs and every major hotel chain every 10 minutes. The second award space opens or a price drops, you get an email instantly."
    },
    {
      icon: "🧠",
      title: "AI tells you exactly what to do",
      desc: "Every alert includes AI analysis — is this a good redemption? Which transfer partner? How long does availability last? No expertise required."
    }
  ];

  return (
    <div style={{
      position:"fixed", inset:0, background:"rgba(0,0,0,.85)",
      display:"flex", alignItems:"center", justifyContent:"center",
      zIndex:400, backdropFilter:"blur(10px)"
    }}>
      <div style={{
        background:"#0f0e0b", border:"1px solid #2a2720",
        borderRadius:16, padding:36, width:500, maxWidth:"92vw",
        boxShadow:"0 40px 100px #000c"
      }}>
        <div style={{ textAlign:"center", marginBottom:32 }}>
          <div style={{ width:48, height:48, borderRadius:12, background:"linear-gradient(135deg,#c9a96e,#a07840)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:22, margin:"0 auto 16px" }}>
            {steps[step].icon}
          </div>
          <div style={{ color:"#f0ede6", fontSize:22, fontFamily:"'Cormorant Garamond', serif", fontWeight:600, marginBottom:12 }}>
            {steps[step].title}
          </div>
          <div style={{ color:"#b0a898", fontSize:14, lineHeight:1.7, maxWidth:380, margin:"0 auto" }}>
            {steps[step].desc}
          </div>
        </div>

        <div style={{ display:"flex", justifyContent:"center", gap:8, marginBottom:28 }}>
          {steps.map((_, i) => (
            <div key={i} style={{ width: i===step?24:8, height:8, borderRadius:4, background: i===step?"#c9a96e":"#2a2720", transition:"all .3s", cursor:"pointer" }} onClick={() => setStep(i)}/>
          ))}
        </div>

        <div style={{ display:"flex", gap:10 }}>
          {step < steps.length - 1 ? (
            <>
              <button onClick={onClose} style={{ flex:1, background:"none", border:"1px solid #2a2720", color:"#6a6458", borderRadius:8, padding:"11px", fontSize:12, fontFamily:"'DM Mono', monospace", cursor:"pointer" }}>
                Skip
              </button>
              <button onClick={() => setStep(s => s+1)} style={{ flex:2, background:"linear-gradient(135deg,#c9a96e,#a07840)", border:"none", color:"#0c0c0e", borderRadius:8, padding:"11px", fontSize:12, fontFamily:"'DM Mono', monospace", cursor:"pointer", fontWeight:500 }}>
                Next →
              </button>
            </>
          ) : (
            <button onClick={onClose} style={{ flex:1, background:"linear-gradient(135deg,#c9a96e,#a07840)", border:"none", color:"#0c0c0e", borderRadius:8, padding:"11px", fontSize:12, fontFamily:"'DM Mono', monospace", cursor:"pointer", fontWeight:500, letterSpacing:"0.06em" }}>
              START WATCHING →
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

function Dashboard() {
  const { signOut } = useClerk();
  const { user } = useUser();
  const [tab, setTab] = useState("watches");
  const [watches, setWatches] = useState(WATCHES);
  const [alerts, setAlerts] = useState(ALERTS);
  const [showAICreator, setShowAICreator] = useState(false);
  const [running, setRunning] = useState(false);
  const [runMsg, setRunMsg] = useState("");
  const [liveMode, setLiveMode] = useState(false);
  const [showWelcome, setShowWelcome] = useState(() => {
    return !localStorage.getItem("vantage_welcomed");
  });

  const handleWelcomeClose = () => {
    localStorage.setItem("vantage_welcomed", "1");
    setShowWelcome(false);
  };

  useEffect(() => {
    fetchLiveData();
    const timer = setInterval(fetchLiveData, 30000);
    return () => clearInterval(timer);
  }, []);

  const fetchLiveData = async () => {
    try {
      const [wRes, aRes] = await Promise.all([
        fetch(`${API_BASE}/watches`),
        fetch(`${API_BASE}/alerts`)
      ]);
      if (wRes.ok) {
        const liveWatches = await wRes.json();
        const mapped = liveWatches.map(w => ({
          id: w.id,
          label: w.label,
          detail: w.type==="airline_award" ? `${w.date||""} · ${w.cabin||"economy"} · ${(w.programs||[]).join(", ")||"All programs"}` : `${w.check_in||""} – ${w.check_out||""} · Award · ${w.program||""}`,
          type: w.type==="airline_award"?"airline":"hotel",
          active: w.active!==false,
          lastCheck: w.updated_at ? new Date(w.updated_at).toLocaleTimeString([],{hour:"2-digit",minute:"2-digit"}) : "—",
          status: "watching",
          programs: w.programs||[]
        }));
        setWatches(mapped);
        setLiveMode(true);
      }
      if (aRes.ok) {
        const liveAlerts = await aRes.json();
        const mapped = liveAlerts.map(a => ({
          id: a.id,
          watch: a.label,
          program: "",
          detail: a.message,
          time: new Date(a.sent_at).toLocaleString([],{month:"short",day:"numeric",hour:"2-digit",minute:"2-digit"}),
          urgent: false
        }));
        if (mapped.length > 0) setAlerts(mapped);
      }
    } catch(e) {}
  };

  const active = watches.filter(w=>w.active).length;
  const newAlerts = alerts.filter(a=>a.urgent).length;

  const handleDelete = async id => {
    setWatches(w => w.filter(x=>x.id!==id));
    try { await fetch(`${API_BASE}/watches/${id}`, {method:"DELETE"}); } catch(e) {}
  };

  const handlePause = async id => {
    setWatches(w => w.map(x=>x.id===id?{...x,active:!x.active,status:x.active?"paused":"watching"}:x));
    try { await fetch(`${API_BASE}/watches/${id}/pause`, {method:"POST"}); } catch(e) {}
  };

  const handleAdd = async watch => {
    const newWatch = {
      id: Math.random().toString(36).slice(2,8),
      label: watch.label || (watch.origin?`${watch.origin} → ${watch.destination}`:watch.property||"New Watch"),
      detail: watch.type==="airline"?`${watch.date||"TBD"} · ${(watch.cabin||"economy").replace(/_/g," ")} · All programs`:`${watch.check_in||"TBD"} – ${watch.check_out||"TBD"} · Award · ${watch.program||"Hyatt"}`,
      type: watch.type, active:true, lastCheck:"just now", status:"watching", programs: watch.programs||[]
    };
    setWatches(w => [newWatch, ...w]);
    try {
      await fetch(`${API_BASE}/watches`, {
        method:"POST", headers:{"Content-Type":"application/json"},
        body: JSON.stringify({ label: newWatch.label, type: watch.type==="airline"?"airline_award":"hotel_award", ...watch })
      });
      await fetchLiveData();
    } catch(e) {}
  };

  const handleRun = async () => {
    setRunning(true);
    setRunMsg("");
    try {
      const res = await fetch(`${API_BASE}/run`, {method:"POST"});
      const data = await res.json();
      setRunMsg(data.status==="completed"?"✓ All watches checked":"⚠ Check logs");
      await fetchLiveData();
    } catch(e) { setRunMsg("✓ Done"); }
    setRunning(false);
    setTimeout(()=>setRunMsg(""),4000);
  };

  return (
    <div style={{ minHeight:"100vh", background:"#0c0c0e", display:"flex" }}>
      <div style={{ width:210, borderRight:"1px solid #161410", display:"flex", flexDirection:"column", padding:"20px 0", flexShrink:0 }}>
        <div style={{ padding:"0 18px 20px", borderBottom:"1px solid #161410", marginBottom:12 }}>
          <div style={{ display:"flex", alignItems:"center", gap:8 }}>
            <div style={{ width:22, height:22, borderRadius:5, background:"linear-gradient(135deg,#c9a96e,#a07840)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:14, color:"#0c0c0e" }}>◈</div>
            <span style={{ color:"#f0ede6", fontSize:15, letterSpacing:"0.25em", fontWeight:500 }}>VANTAGE</span>
          </div>
        </div>
        {[{id:"watches",label:"Watches",count:active},{id:"calendar",label:"Calendar",count:0},{id:"alerts",label:"Alerts",count:newAlerts},{id:"settings",label:"Settings",count:0}].map(item=>(
          <button key={item.id} onClick={()=>setTab(item.id)} style={{ background:tab===item.id?"#1e1c18":"none", border:"none", borderLeft:tab===item.id?"2px solid #c9a96e":"2px solid transparent", color:tab===item.id?"#e8e4dc":"#a09888", padding:"9px 18px", textAlign:"left", fontSize:14, fontFamily:"'DM Mono', monospace", letterSpacing:"0.08em", cursor:"pointer", display:"flex", justifyContent:"space-between", alignItems:"center", transition:"all .15s" }}>
            {item.label.toUpperCase()}
            {item.count>0&&<span style={{ background:item.id==="alerts"?"#3d8c5a":"#c9a96e", color:"#0c0c0e", borderRadius:10, padding:"1px 6px", fontSize:14, fontWeight:700 }}>{item.count}</span>}
          </button>
        ))}
        <div style={{flex:1}}/>
        <div style={{ padding:"0 14px", marginBottom:12 }}>
          <div style={{ background:"#111008", border:"1px solid #1e1c18", borderRadius:8, padding:"10px 12px", display:"flex", alignItems:"center", justifyContent:"space-between" }}>
            <div>
              <div style={{ color:"#e8e4dc", fontSize:10 }}>{user?.firstName || user?.emailAddresses?.[0]?.emailAddress?.split("@")[0] || "Account"}</div>
              <div style={{ color:"#d0c8b0", fontSize:14, marginTop:1 }}>Free plan</div>
            </div>
            <button onClick={() => signOut()} style={{ background:"none", border:"1px solid #2a2720", color:"#d0c8b8", borderRadius:5, padding:"3px 8px", fontSize:14, fontFamily:"'DM Mono', monospace", cursor:"pointer" }}>Out</button>
          </div>
        </div>
        <div style={{ padding:"0 14px" }}>
          <div style={{ background:"#111008", border:"1px solid #1e1c18", borderRadius:8, padding:12 }}>
            <div style={{ color:"#d0c8b0", fontSize:14, letterSpacing:"0.1em", marginBottom:5 }}>FREE PLAN</div>
            <div style={{ color:"#f0ede6", fontSize:15, marginBottom:7 }}>{active} of 3 watches</div>
            <div style={{ height:2, background:"#1e1c18", borderRadius:1, marginBottom:9 }}>
              <div style={{ width:`${Math.min((active/3)*100,100)}%`, height:"100%", background:"linear-gradient(90deg,#c9a96e,#a07840)", borderRadius:1 }}/>
            </div>
            <button style={{ width:"100%", background:"linear-gradient(135deg,#c9a96e,#a07840)", border:"none", color:"#0c0c0e", borderRadius:5, padding:"6px", fontSize:14, fontFamily:"'DM Mono', monospace", cursor:"pointer", fontWeight:500, letterSpacing:"0.08em" }}>UPGRADE →</button>
          </div>
        </div>
      </div>
      <div style={{ flex:1, display:"flex", flexDirection:"column", minWidth:0 }}>
        <div style={{ borderBottom:"1px solid #161410", padding:"16px 24px", display:"flex", justifyContent:"space-between", alignItems:"center" }}>
          <div>
            <div style={{ color:"#e8e4dc", fontSize:15, fontFamily:"'Cormorant Garamond', serif", fontWeight:600, letterSpacing:"0.02em" }}>
              {tab==="watches"?"Active Watches":tab==="alerts"?"Alert History":tab==="calendar"?"Availability Calendar":"Settings"}
            </div>
            <div style={{ display:"flex", alignItems:"center", gap:8, marginTop:2 }}>
              <span style={{ color:"#d0c8b8", fontSize:9 }}>{tab==="watches"?`${active} watching · every 10 min`:`${alerts.length} alerts`}</span>
              {liveMode && <span style={{ display:"inline-flex", alignItems:"center", gap:4, background:"#3d8c5a15", border:"1px solid #3d8c5a30", borderRadius:10, padding:"1px 7px" }}>
                <Dot color="#3d8c5a" pulse size={5}/>
                <span style={{ color:"#3d8c5a", fontSize:14, letterSpacing:"0.08em" }}>LIVE</span>
              </span>}
            </div>
          </div>
          <div style={{ display:"flex", alignItems:"center", gap:8 }}>
            {runMsg && <span style={{ color:"#3d8c5a", fontSize:10 }}>{runMsg}</span>}
            <button onClick={handleRun} disabled={running} style={{ background:"none", border:"1px solid #2a2720", color:running?"#9a9080":"#b0a898", borderRadius:7, padding:"7px 14px", fontSize:14, fontFamily:"'DM Mono', monospace", cursor:running?"not-allowed":"pointer", letterSpacing:"0.06em", display:"flex", alignItems:"center", gap:6 }}>
              {running&&<div style={{ width:10, height:10, border:"1.5px solid #3a3830", borderTopColor:"#b0a898", borderRadius:"50%", animation:"spin 1s linear infinite" }}/>}
              {running?"CHECKING...":"▶ RUN NOW"}
            </button>
            {tab==="watches"&&<button onClick={()=>setShowAICreator(true)} style={{ background:"linear-gradient(135deg,#c9a96e,#a07840)", border:"none", color:"#0c0c0e", borderRadius:7, padding:"7px 14px", fontSize:14, fontFamily:"'DM Mono', monospace", cursor:"pointer", fontWeight:500, letterSpacing:"0.06em" }}>◈ AI + ADD WATCH</button>}
          </div>
        </div>
        <div style={{ flex:1, padding:"16px 24px", overflowY:"auto" }}>
          {tab==="watches"&&(
            <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
              {watches.length===0?(
                <div style={{ textAlign:"center", padding:"60px 0", color:"#d0c8b0" }}>
                  <div style={{ fontSize:28, marginBottom:10 }}>◈</div>
                  <div style={{ fontSize:14, marginBottom:14 }}>No watches yet</div>
                  <button onClick={()=>setShowAICreator(true)} style={{ background:"none", border:"1px solid #c9a96e30", color:"#c9a96e", borderRadius:7, padding:"7px 16px", fontSize:14, fontFamily:"'DM Mono', monospace", cursor:"pointer" }}>◈ Create with AI</button>
                </div>
              ):watches.map(w=><WatchCard key={w.id} watch={w} onDelete={handleDelete} onPause={handlePause}/>)}
            </div>
          )}
          {tab==="calendar"&&<CalendarView/>}
          {tab==="alerts"&&<div>{alerts.map(a=><AlertRow key={a.id} alert={a}/>)}</div>}
          {tab==="settings"&&(
            <div style={{ maxWidth:460 }}>
              <div style={{ background:"#111008", border:"1px solid #1e1c18", borderRadius:9, padding:18, marginBottom:14 }}>
                <div style={{ color:"#d0c8b0", fontSize:14, letterSpacing:"0.1em", marginBottom:12 }}>NOTIFICATIONS</div>
                {[["Email","Connected","✓","#3d8c5a"],["SMS","Not configured","—","#9a9080"],["Push","Coming soon","—","#9a9080"]].map(([t,d,s,sc])=>(
                  <div key={t} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", padding:"9px 0", borderBottom:"1px solid #161410" }}>
                    <div><div style={{ color:"#e8e4dc", fontSize:11 }}>{t}</div><div style={{ color:"#d0c8b0", fontSize:14, marginTop:1 }}>{d}</div></div>
                    <span style={{ color:sc, fontSize:11 }}>{s}</span>
                  </div>
                ))}
              </div>
              <div style={{ background:"#111008", border:"1px solid #1e1c18", borderRadius:9, padding:18 }}>
                <div style={{ color:"#d0c8b0", fontSize:14, letterSpacing:"0.1em", marginBottom:12 }}>CHECK INTERVAL</div>
                {["5 min","10 min","30 min","1 hour"].map(t=>(
                  <button key={t} style={{ marginRight:8, marginBottom:8, background:t==="10 min"?"#c9a96e12":"#0c0c0e", border:`1px solid ${t==="10 min"?"#c9a96e40":"#1e1c18"}`, color:t==="10 min"?"#c9a96e":"#a09888", borderRadius:6, padding:"5px 11px", fontSize:14, fontFamily:"'DM Mono', monospace", cursor:"pointer" }}>{t}</button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>
      {showAICreator&&<AIWatchCreator onAdd={handleAdd} onClose={()=>setShowAICreator(false)}/>}
      {showWelcome&&<WelcomeModal onClose={handleWelcomeClose}/>}
    </div>
  );
}

function AuthScreen({ mode, onToggle, onBack }) {
  const { signIn } = useSignIn();
  const { signUp } = useSignUp();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [pendingVerification, setPendingVerification] = useState(false);
  const [code, setCode] = useState("");

  const handleSubmit = async () => {
    setLoading(true);
    setError("");
    try {
      if (mode === "sign-in") {
        await signIn.create({ identifier: email, password });
        window.location.reload();
      } else {
        console.log("Attempting signup:", email);
        const result = await signUp.create({ emailAddress: email, password, firstName: name });
        console.log("Signup result:", JSON.stringify(result));
        await signUp.prepareEmailAddressVerification({ strategy: "email_code" });
        setPendingVerification(true);
      }
    } catch(e) {
      console.error("Auth error full:", JSON.stringify(e), e.message, e.errors);
      setError(e.errors?.[0]?.longMessage || e.errors?.[0]?.message || e.message || JSON.stringify(e));
    }
    setLoading(false);
  };

  const handleVerify = async () => {
    setLoading(true);
    try {
      await signUp.attemptEmailAddressVerification({ code });
      window.location.reload();
    } catch(e) {
      setError(e.errors?.[0]?.message || "Invalid code");
    }
    setLoading(false);
  };

  const handleGoogle = async () => {
    try {
      if (mode === "sign-in") {
        await signIn.authenticateWithRedirect({ strategy:"oauth_google", redirectUrl:"/sso-callback", redirectUrlComplete:"/" });
      } else {
        await signUp.authenticateWithRedirect({ strategy:"oauth_google", redirectUrl:"/sso-callback", redirectUrlComplete:"/" });
      }
    } catch(e) { setError(e.errors?.[0]?.message || "Google auth failed"); }
  };

  const inp = { width:"100%", background:"#0c0c0e", border:"1px solid #2a2720", borderRadius:8, padding:"11px 13px", color:"#f0ede6", fontSize:14, fontFamily:"DM Mono, monospace", marginBottom:10 };

  return (
    <div style={{ minHeight:"100vh", background:"#0c0c0e", display:"flex", alignItems:"center", justifyContent:"center", position:"relative", overflow:"hidden" }}>
      <div style={{ position:"absolute", inset:0, opacity:.03, backgroundImage:"linear-gradient(#c9a96e 1px,transparent 1px),linear-gradient(90deg,#c9a96e 1px,transparent 1px)", backgroundSize:"80px 80px", pointerEvents:"none" }}/>
      <div style={{ position:"absolute", top:"20%", left:"50%", transform:"translateX(-50%)", width:600, height:400, borderRadius:"50%", background:"radial-gradient(ellipse,#c9a96e06 0%,transparent 70%)", pointerEvents:"none" }}/>
      <div style={{ position:"relative", zIndex:10, width:"100%", maxWidth:400, padding:"0 24px" }}>
        <button onClick={onBack} style={{ position:"absolute", top:-60, left:0, background:"none", border:"none", color:"#d0c8b8", fontSize:14, fontFamily:"DM Mono, monospace", cursor:"pointer", letterSpacing:"0.06em" }}>← Back</button>
        <div style={{ textAlign:"center", marginBottom:32 }}>
          <div style={{ display:"inline-flex", alignItems:"center", gap:8, marginBottom:16 }}>
            <div style={{ width:28, height:28, borderRadius:7, background:"linear-gradient(135deg,#c9a96e,#a07840)", display:"flex", alignItems:"center", justifyContent:"center", fontSize:15, color:"#0c0c0e" }}>◈</div>
            <span style={{ color:"#f0ede6", fontSize:15, letterSpacing:"0.25em", fontWeight:500 }}>VANTAGE</span>
          </div>
          <div style={{ color:"#f0ede6", fontSize:28, fontFamily:"Cormorant Garamond, serif", fontWeight:300, marginBottom:6 }}>
            {pendingVerification ? "Check your email" : mode==="sign-in" ? "Welcome back" : "Start watching"}
          </div>
          <div style={{ color:"#d8d0c0", fontSize:11 }}>
            {pendingVerification ? "Enter the code we sent you" : mode==="sign-in" ? "Sign in to your account" : "Free forever. No credit card."}
          </div>
        </div>

        <div style={{ background:"#111008", border:"1px solid #2a2720", borderRadius:14, padding:24, boxShadow:"0 40px 80px #000a" }}>
          {pendingVerification ? (
            <>
              <div style={{ color:"#d8d0c0", fontSize:14, marginBottom:14 }}>Verification code</div>
              <input value={code} onChange={e=>setCode(e.target.value)} placeholder="6-digit code" style={inp}/>
              {error && <div style={{ color:"#c87070", fontSize:14, marginBottom:10 }}>{error}</div>}
              <button onClick={handleVerify} disabled={loading} style={{ width:"100%", background:"linear-gradient(135deg,#c9a96e,#a07840)", border:"none", color:"#0c0c0e", borderRadius:8, padding:"12px", fontSize:14, fontFamily:"DM Mono, monospace", cursor:"pointer", fontWeight:500, letterSpacing:"0.06em" }}>
                {loading ? "VERIFYING..." : "VERIFY EMAIL"}
              </button>
            </>
          ) : (
            <>
              <button onClick={handleGoogle} style={{ width:"100%", background:"#1e1c18", border:"1px solid #2a2720", color:"#e8e4dc", borderRadius:8, padding:"11px", fontSize:14, fontFamily:"DM Mono, monospace", cursor:"pointer", marginBottom:16, display:"flex", alignItems:"center", justifyContent:"center", gap:8 }}>
                <span style={{ fontSize:14 }}>G</span> Continue with Google
              </button>
              <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:16 }}>
                <div style={{ flex:1, height:1, background:"#1e1c18" }}/>
                <span style={{ color:"#d0c8b0", fontSize:10 }}>or</span>
                <div style={{ flex:1, height:1, background:"#1e1c18" }}/>
              </div>
              {mode === "sign-up" && <input value={name} onChange={e=>setName(e.target.value)} placeholder="First name" style={inp}/>}
              <input value={email} onChange={e=>setEmail(e.target.value)} placeholder="Email address" type="email" style={inp}/>
              <input value={password} onChange={e=>setPassword(e.target.value)} placeholder="Password" type="password" style={{...inp, marginBottom:0}}
                onKeyDown={e => e.key==="Enter" && handleSubmit()}/>
              {error && <div style={{ color:"#c87070", fontSize:14, marginTop:10 }}>{error}</div>}
              <button onClick={handleSubmit} disabled={loading} style={{ width:"100%", background:"linear-gradient(135deg,#c9a96e,#a07840)", border:"none", color:"#0c0c0e", borderRadius:8, padding:"12px", fontSize:14, fontFamily:"DM Mono, monospace", cursor:"pointer", fontWeight:500, letterSpacing:"0.06em", marginTop:14 }}>
                {loading ? "..." : mode==="sign-in" ? "SIGN IN" : "CREATE ACCOUNT"}
              </button>
            </>
          )}
        </div>
        <div style={{ textAlign:"center", marginTop:20 }}>
          <span style={{ color:"#d0c8b8", fontSize:11 }}>{mode==="sign-in"?"No account? ":"Already have an account? "}</span>
          <span onClick={onToggle} style={{ color:"#c9a96e", fontSize:14, cursor:"pointer" }}>{mode==="sign-in"?"Sign up free":"Sign in"}</span>
        </div>
      </div>
    </div>
  );
}

function AppInner() {
  const { isSignedIn, isLoaded } = useUser();
  const [view, setView] = useState("landing");
  const [authMode, setAuthMode] = useState("sign-in");

  useEffect(() => {
    if (isLoaded && isSignedIn) setView("dashboard");
  }, [isLoaded, isSignedIn]);

  if (!isLoaded) return (
    <div style={{ minHeight:"100vh", background:"#0c0c0e", display:"flex", alignItems:"center", justifyContent:"center" }}>
      <div style={{ width:20, height:20, border:"2px solid #2a2720", borderTopColor:"#c9a96e", borderRadius:"50%", animation:"spin 1s linear infinite" }}/>
      <style>{CSS}</style>
    </div>
  );

  return (
    <>
      <style>{FONT}{CSS}</style>
      {view==="landing" && <Landing onEnter={() => isSignedIn ? setView("dashboard") : setView("auth")} />}
      {view==="auth" && <AuthScreen mode={authMode} onToggle={() => setAuthMode(m => m==="sign-in"?"sign-up":"sign-in")} onBack={() => setView("landing")} />}
      {view==="dashboard" && <Dashboard />}
    </>
  );
}

export default function App() {
  return (
    <ClerkProvider publishableKey={CLERK_KEY}>
      <AppInner />
    </ClerkProvider>
  );
}
